import React, { useState, useEffect } from 'react';
import { fetchQualityMetrics, addToPriorityQueue } from '../../utils/api';
import ProblemsOnlyToggle from '../common/ProblemsOnlyToggle';
import { Button } from '../ui/button';

function QualityMetrics({ unitId, serviceLineId, level }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProblemsOnly, setShowProblemsOnly] = useState(false);

  useEffect(() => {
    const loadMetrics = async () => {
      setLoading(true);
      const data = await fetchQualityMetrics({ unitId, serviceLineId, level });
      setMetrics(data);
      setLoading(false);
    };

    loadMetrics();
  }, [unitId, serviceLineId, level]);

  const handleAddToQueue = async (payload) => {
    try {
      await addToPriorityQueue({
        title: payload.title,
        description: payload.metric || '',
        category: 'Quality',
        priority: payload.priority || 'medium',
        estimatedTime: 45,
        dueDate: new Date().toISOString(),
        type: 'task',
        ...payload
      });
    } catch (err) {
      console.error('Add to queue failed:', err);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-lg shadow p-6">Loading quality metrics...</div>;
  }

  const problemCount = (metrics || []).filter(m => m.current < m.target).length;
  const visibleMetrics = showProblemsOnly
    ? (metrics || []).filter(m => m.current < m.target)
    : (metrics || []);

  return (
    <div className="dashboard-container">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h3 className="font-semibold text-xl mb-2">Consolidated Quality Metrics</h3>
          <p className="text-sm text-gray-600">
            All your metrics in one place - no more jumping between Tableau, PowerBI, and Insight
          </p>
        </div>
        <ProblemsOnlyToggle
          enabled={showProblemsOnly}
          onChange={setShowProblemsOnly}
          count={problemCount}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleMetrics.map(metric => {
          const trendClass = metric.trend > 0 ? 'positive' : metric.trend < 0 ? 'negative' : 'neutral';
          const borderClass = metric.current >= metric.target ? 'border-l-4 border-l-green-nn' : metric.current >= metric.target * 0.9 ? 'border-l-4 border-l-yellow-nn' : 'border-l-4 border-l-red-critical';
          return (
            <div key={metric.id} className={`bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative ${borderClass}`}>
              <div className="p-5">
                <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">{metric.name}</div>
                <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{metric.current}%</div>
                <div className={`flex items-center gap-1 text-[13px] mb-4 ${trendClass === 'positive' ? 'text-green-nn' : trendClass === 'negative' ? 'text-red-critical' : 'text-text-dark-secondary'}`}>
                  {metric.trendLabel}
                </div>
                <div className="text-xs text-gray-500 mb-2">Target: {metric.target}%</div>
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                  <Button
                    variant="default"
                    size="sm"
                    type="button"
                    onClick={() => handleAddToQueue({
                      title: `Address ${metric.name} (${metric.current}%)`,
                      metric: metric.name,
                      priority: metric.current >= metric.target * 0.9 ? 'medium' : 'high'
                    })}
                  >
                    Add to Queue
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default QualityMetrics;
